/**
 * Reel Generation State Management
 * Feature 017: Instagram Reel Creation
 * Reducer for managing complex reel generation state
 */

import { useReducer } from 'react';
import type { IReelGenerationState, ReelGenerationAction, ModelKey } from '../@types/reels';
import type { Id } from '../../convex/_generated/dataModel';

/**
 * Initial state for reel generation
 */
const initialState: IReelGenerationState = {
  phase: 'idle',
  researchResults: null,
  researchCached: false,
  researchLoading: false,
  researchError: null,
  selectedModels: [],
  generationStatus: {
    claude: 'idle',
    gemini: 'idle',
    openai: 'idle',
  },
  generatedReels: {} as Record<ModelKey, never>,
  selectedVariantModel: null,
  isSelecting: false,
  reelId: null,
};

/**
 * Reel Generation Reducer
 * Handles all state transitions for research + 3-model generation flow
 */
function reelGenerationReducer(
  state: IReelGenerationState,
  action: ReelGenerationAction
): IReelGenerationState {
  switch (action.type) {
    case 'INIT_REEL':
      return {
        ...initialState,
        reelId: action.payload.reelId,
        phase: 'idle',
        // Preserve selectedModels and generationStatus from current state
        selectedModels: state.selectedModels,
        generationStatus: state.generationStatus,
      };

    case 'START_RESEARCH':
      return {
        ...state,
        phase: 'research',
        researchLoading: true,
        researchError: null,
      };

    case 'RESEARCH_SUCCESS':
      return {
        ...state,
        phase: 'research',
        researchLoading: false,
        researchResults: action.payload.researchResults,
        researchCached: action.payload.cached,
        researchError: null,
      };

    case 'RESEARCH_ERROR':
      return {
        ...state,
        phase: 'error',
        researchLoading: false,
        researchError: action.payload.error,
      };

    case 'SET_SELECTED_MODELS': {
      // Build generationStatus, preserving existing non-idle statuses
      const generationStatus: Record<ModelKey, 'idle' | 'loading' | 'completed' | 'error'> = {
        claude: 'idle',
        gemini: 'idle',
        openai: 'idle',
      };
      action.payload.models.forEach(model => {
        generationStatus[model] = state.generationStatus[model] || 'idle';
      });

      return {
        ...state,
        selectedModels: action.payload.models,
        generationStatus,
      };
    }

    case 'START_GENERATION':
      return {
        ...state,
        phase: 'generation',
        generatedReels: {} as Record<ModelKey, never>,
      };

    case 'MODEL_GENERATION_START':
      return {
        ...state,
        generationStatus: {
          ...state.generationStatus,
          [action.payload.modelKey]: 'loading',
        },
      };

    case 'MODEL_GENERATION_SUCCESS':
      return {
        ...state,
        generationStatus: {
          ...state.generationStatus,
          [action.payload.modelKey]: 'completed',
        },
        generatedReels: {
          ...state.generatedReels,
          [action.payload.modelKey]: action.payload.reelData,
        },
      };

    case 'MODEL_GENERATION_ERROR':
      return {
        ...state,
        generationStatus: {
          ...state.generationStatus,
          [action.payload.modelKey]: 'error',
        },
        generatedReels: {
          ...state.generatedReels,
          [action.payload.modelKey]: {
            content: '',
            model: action.payload.modelKey,
            timestamp: Date.now(),
            cached: false,
            error: action.payload.error,
          },
        },
      };

    case 'ALL_MODELS_COMPLETE':
      return {
        ...state,
        phase: 'completed',
      };

    case 'START_VARIANT_SELECTION':
      return {
        ...state,
        isSelecting: true,
      };

    case 'VARIANT_SELECTION_SUCCESS':
      return {
        ...state,
        selectedVariantModel: action.payload.modelKey,
        isSelecting: false,
      };

    case 'VARIANT_SELECTION_ERROR':
      return {
        ...state,
        isSelecting: false,
        researchError: action.payload.error,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

/**
 * Custom hook for reel generation state
 * Returns state and dispatch function
 */
export function useReelGenerationState(reelId?: Id<'reels'>) {
  const [state, dispatch] = useReducer(reelGenerationReducer, {
    ...initialState,
    reelId: reelId || null,
  });

  return { state, dispatch };
}
